import React, { Fragment, useContext } from 'react';
import { StateConfigContext } from '../../../../../Context/StateConfigContext';
import {
  ConditionalIf,
  CreatorUserType,
  Field,
  FieldValueType,
  GeneralInformationValueType,
  LocationAreaType,
  ReportConfig,
  Section,
  StateRelatedValueType
} from '../../../../../Models';
import { FieldItem } from '../../CommonBody/FieldItem/FieldItem';
import styles from './GeneralInformation.module.scss';
import { SectionField } from '../../CommonBody/SectionField/SectionField';
import {
  FieldItemList,
  FieldItemListType
} from '../../CommonBody/FieldItemList/FieldItemList';
import { isValueEmpty } from '../../../../../Helpers';

export interface GeneralInformationProps {
  report: GeneralInformationValueType;
}

const getDateAndLocationFields = (
  report: GeneralInformationValueType,
  locationType: Field
): FieldItemListType[] => {
  let fields: FieldItemListType[] = [
    {
      label: 'Date',
      value: report['date'] as FieldValueType,
      type: 'date'
    },
    {
      label: 'Time',
      value: report['date'] as FieldValueType,
      type: 'time'
    }
  ];

  if (report['location'].address && report['location'].address.full) {
    fields = [
      ...fields,
      {
        label: 'Location',
        value: report['location'].address?.full,
        type: 'string'
      }
    ];
  }

  if (report['location'].areas && report['location'].areas.length > 0) {
    fields = [
      ...fields,
      {
        label: 'Areas',
        value: report['location'].areas.map(
          (area: LocationAreaType) => area.name
        ),
        type: 'array'
      }
    ];
  }

  fields = [
    ...fields,
    {
      label: 'Location Details',
      value: report['location'].details,
      type: 'string'
    },
    {
      label: 'Location Type',
      value: report['location'].type,
      type: 'string'
    }
  ];

  /**
   * Location type config dynamic fields - currently not supported
   * Render fields on inside conditionals
   */
  if (locationType && locationType.condition_if) {
    for (let conditional of locationType.condition_if) {
      if (
        (conditional as ConditionalIf).selected_option ===
        report['location'].type
      ) {
        for (let field of (conditional as ConditionalIf).fields) {
          if (
            report['location'].type_value &&
            report['location']['type_value'][field.name]
          ) {
            fields = [
              ...fields,
              {
                label: field.label,
                value: report['location']['type_value'][
                  field.name
                ] as FieldValueType,
                type: field.type
              }
            ];
            //Add support for nested conditionals if needed - just like above 1 level
            if (field.condition_if) {
              for (let conditional of field.condition_if) {
                if (
                  (conditional as ConditionalIf).selected_option ===
                  report['location']['type_value'][field.name]
                ) {
                  for (let nestedField of (conditional as ConditionalIf)
                    .fields) {
                    if (
                      report['location']['type_value'] &&
                      report['location']['type_value'][nestedField.name]
                    ) {
                      fields = [
                        ...fields,
                        {
                          label: nestedField.label,
                          value: report['location']['type_value'][
                            nestedField.name
                          ] as FieldValueType,
                          type: nestedField.type
                        }
                      ];
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  return fields;
};

export const GeneralInformation = ({
  report
}: GeneralInformationProps): React.ReactElement<GeneralInformationProps> => {
  const stateConfig = useContext(StateConfigContext)
    .stateConfig as ReportConfig;
  const creatorUserGroups = report.created_by_user
    ? (report.created_by_user as CreatorUserType).groups
    : undefined;

  const connectReportsSection = stateConfig.general_information.sections.filter(
    (section: Section) => section.name === 'to_connect_reports'
  );

  const stateRelatedSections = stateConfig.general_information.sections.filter(
    (section: Section) => section.name !== 'to_connect_reports'
  );

  const isStateRelatedSectionValueEmpty = (
    value: StateRelatedValueType
  ): boolean => {
    const isObjectValueEmpty = (val: any): boolean => {
      let isEmpty = true;
      Object.keys(val).forEach((key) => {
        if (!isValueEmpty(val[key])) {
          isEmpty = false;
        }
      });
      return isEmpty;
    };

    return isValueEmpty(value) || isObjectValueEmpty(value);
  };

  return (
    <div className={styles.generalInformation}>
      {stateConfig && (
        <Fragment>
          <h3 className={styles.title}>
            {stateConfig.general_information.title}
          </h3>
          <div className={styles.items}>
            <FieldItem
              label={'Incident Number'}
              value={report['incident_number'] as FieldValueType}
              type="string"
            />
            {/*ID to Link Reports | CAD Number */}
            {connectReportsSection.map((section: Section) => {
              if (report.state_related[section.name]) {
                return (
                  <SectionField
                    key={section.name}
                    config={section}
                    value={report.state_related[section.name]}
                  />
                );
              }

              return undefined;
            })}
            <FieldItem
              label={'Report Type'}
              value={report['type'] as FieldValueType}
              type="string"
            />
            {report.created_by_user?.title && (
              <FieldItem
                label="Officer Title"
                value={report.created_by_user.title.name}
                type="string"
              />
            )}
            <FieldItem
              label={'Officer Name'}
              value={report['officer_name'] as FieldValueType}
              type="string"
            />
            <FieldItem
              label={'Officer ID'}
              value={report['officer_id_number'] as FieldValueType}
              type="string"
            />
            {creatorUserGroups && creatorUserGroups.length > 0 && (
              <FieldItem
                label="Officer Groups"
                value={creatorUserGroups.map((val) => val.name)}
                type="array"
              />
            )}
            <FieldItemList
              title="Date and Location"
              fields={getDateAndLocationFields(
                report,
                stateConfig.location_type
              )}
            />
            {report['lighting_conditions'] &&
              (report['lighting_conditions'] as string[]).length !== 0 && (
                <FieldItemList
                  title="Lighting Conditions Details"
                  fields={[
                    {
                      label: 'Lighting Conditions',
                      value: report[
                        'lighting_conditions'
                      ] as FieldItemListType[],
                      type: 'array'
                    }
                  ]}
                />
              )}
            {report['was_use_of_force'] && (
              <FieldItem
                label={'Was Use Of Force'}
                value={report['was_use_of_force'] as FieldValueType}
                type="boolean"
              />
            )}
            {stateRelatedSections.map((section: Section) => {
              if (
                report.state_related[section.name] &&
                !isStateRelatedSectionValueEmpty(
                  report.state_related[section.name] as StateRelatedValueType
                )
              ) {
                return (
                  <SectionField
                    key={section.name}
                    config={section}
                    value={report.state_related[section.name]}
                  />
                );
              }

              return undefined;
            })}
          </div>
        </Fragment>
      )}
    </div>
  );
};
